import api from './api';
import { readVatClassification, toVatPayload } from '../utils/vatClassification';
import { normalizeLanguage } from '../constants/Languages';
import { normalizeCustomerPaymentMode } from '../constants/CustomerPaymentMode';
import { PrepaymentMode, normalizeDepositType, normalizePrepaymentMode } from '../constants/PrepaymentMode';
import {
  isFreeServicePrice,
  normalizeFreeServiceUsageLimit,
} from '../utils/freeServiceUtils';
import {
  ServiceValueMode,
  normalizeServiceValueMode,
} from '../utils/serviceValueFormat';

export { isCustomerBookableService, isFreeServicePrice, normalizeFreeServiceUsageLimit } from '../utils/freeServiceUtils';

const DEFAULT_DURATION_MINUTES = 30;
const DEFAULT_CURRENCY = 'EUR';
const SERVICE_TYPE_PRIMARY = 'Primary';
const SERVICE_TYPE_SECONDARY = 'Secondary';

const normalizeSupportedLanguages = (supportedLanguages = []) => {
  const languages = supportedLanguages
    .map((language) => normalizeLanguage(language))
    .filter((language, index, array) => array.indexOf(language) === index);

  return languages.length > 0 ? languages : ['en'];
};

const normalizeLocalizedDictionary = (value) => {
  if (!value || typeof value !== 'object') {
    return {};
  }

  return Object.entries(value).reduce((accumulator, [key, entryValue]) => {
    if (typeof entryValue !== 'string') {
      return accumulator;
    }

    accumulator[normalizeLanguage(key)] = entryValue;
    return accumulator;
  }, {});
};

const parseDurationToMinutes = (duration) => {
  if (typeof duration === 'number' && Number.isFinite(duration)) {
    return duration;
  }

  if (typeof duration !== 'string' || !duration) {
    return DEFAULT_DURATION_MINUTES;
  }

  const parts = duration.split(':');
  if (parts.length < 2) {
    return DEFAULT_DURATION_MINUTES;
  }

  const hours = Number.parseInt(parts[0], 10) || 0;
  const minutes = Number.parseInt(parts[1], 10) || 0;

  return (hours * 60) + minutes;
};

const formatDurationForBackend = (minutes) => {
  const parsedMinutes = Number.parseInt(minutes, 10);
  const safeMinutes = Number.isFinite(parsedMinutes) && parsedMinutes > 0
    ? parsedMinutes
    : DEFAULT_DURATION_MINUTES;
  const hours = Math.floor(safeMinutes / 60);
  const remainingMinutes = safeMinutes % 60;

  return `${hours.toString().padStart(2, '0')}:${remainingMinutes.toString().padStart(2, '0')}`;
};

const normalizeServiceType = (type) => {
  if (type === 2 || `${type}`.toLowerCase() === SERVICE_TYPE_SECONDARY.toLowerCase()) {
    return SERVICE_TYPE_SECONDARY;
  }

  return SERVICE_TYPE_PRIMARY;
};

const normalizeStandaloneForType = (type, isStandalone) => (
  normalizeServiceType(type) === SERVICE_TYPE_PRIMARY ? true : Boolean(isStandalone)
);

const buildLocalizedPayload = (value) => (
  Object.fromEntries(
    Object.entries(normalizeLocalizedDictionary(value)).map(([language, entryValue]) => [language.toUpperCase(), entryValue.trim()])
  )
);

// A deposit value only means something alongside a prepayment mode, so it is cleared otherwise
// to keep a later mode switch from resurrecting a stale value.
const buildPrepaymentPayload = (service) => {
  const prepaymentMode = normalizePrepaymentMode(service.prepaymentMode);
  const depositValue = Number(service.depositValue);
  const hasDeposit = prepaymentMode !== PrepaymentMode.NotRequired && Number.isFinite(depositValue) && depositValue > 0;

  return {
    prepaymentMode,
    depositType: hasDeposit ? normalizeDepositType(service.depositType) : null,
    depositValue: hasDeposit ? depositValue : null,
  };
};

const buildServicePayload = (service, serviceId = null) => {
  const priceMode = normalizeServiceValueMode(service.priceMode);
  const durationMode = normalizeServiceValueMode(service.durationMode);
  const fixedPrice = priceMode === ServiceValueMode.Fixed;
  const free = fixedPrice && isFreeServicePrice(service.price);
  const pricedService = free || !fixedPrice
    ? { ...service, prepaymentMode: PrepaymentMode.NotRequired, depositValue: null, allowInstallments: false }
    : service;

  return {
    id: Number.isInteger(serviceId) && serviceId > 0
      ? serviceId
      : (Number.isInteger(service.id) && service.id > 0 ? service.id : null),
    name: buildLocalizedPayload(service.name),
    description: buildLocalizedPayload(service.description),
    type: normalizeServiceType(service.type),
    isStandalone: normalizeStandaloneForType(service.type, service.isStandalone),
    priceMode,
    durationMode,
    duration: durationMode === ServiceValueMode.OnRequest ? '00:00' : formatDurationForBackend(service.duration),
    durationMax: durationMode === ServiceValueMode.Range ? formatDurationForBackend(service.durationMax) : null,
    price: priceMode === ServiceValueMode.OnRequest ? 0 : (Number(service.price) || 0),
    priceMax: priceMode === ServiceValueMode.Range && service.priceMax !== '' && service.priceMax != null
      ? Number(service.priceMax)
      : null,
    usageLimit: fixedPrice ? normalizeFreeServiceUsageLimit(service.price, service.usageLimit) : null,
    visibleToCustomers: service.visibleToCustomers !== false,
    disabled: Boolean(service.disabled),
    ...buildPrepaymentPayload(pricedService),
    allowInstallments: pricedService.allowInstallments === true,
    teamMemberIds: Array.isArray(service.teamMemberIds) ? service.teamMemberIds : [],
    consentFormId: service.consentFormId || null,
    products: Array.isArray(service.products)
      ? service.products.map((line) => ({
        productId: line.productId,
        quantity: Number(line.quantity),
      }))
      : null,
    ...toVatPayload(service),
  };
};

const toFormService = (service) => ({
  id: service?.id ?? null,
  name: normalizeLocalizedDictionary(service?.name),
  description: normalizeLocalizedDictionary(service?.description),
  type: normalizeServiceType(service?.type),
  isStandalone: normalizeStandaloneForType(service?.type, service?.isStandalone),
  isCombo: Boolean(service?.isCombo ?? service?.IsCombo),
  duration: parseDurationToMinutes(service?.duration),
  durationMax: service?.durationMax ?? service?.DurationMax
    ? parseDurationToMinutes(service?.durationMax ?? service?.DurationMax)
    : '',
  durationMode: normalizeServiceValueMode(service?.durationMode ?? service?.DurationMode),
  price: Number(service?.price) || 0,
  priceMax: service?.priceMax ?? service?.PriceMax ?? '',
  priceMode: normalizeServiceValueMode(service?.priceMode ?? service?.PriceMode),
  includedInPlanOrPackage: Boolean(service?.includedInPlanOrPackage ?? service?.IncludedInPlanOrPackage),
  usageLimit: service?.usageLimit ?? service?.UsageLimit ?? '',
  visibleToCustomers: (service?.visibleToCustomers ?? service?.VisibleToCustomers) !== false,
  disabled: Boolean(service?.disabled),
  prepaymentMode: normalizePrepaymentMode(service?.prepaymentMode ?? service?.PrepaymentMode),
  depositType: normalizeDepositType(service?.depositType ?? service?.DepositType),
  depositValue: service?.depositValue ?? service?.DepositValue ?? '',
  allowInstallments: (service?.allowInstallments ?? service?.AllowInstallments) === true,
  teamMemberIds: Array.isArray(service?.teamMemberIds) ? [...service.teamMemberIds] : [],
  consentFormId: service?.consentFormId ?? service?.ConsentFormId ?? null,
  ...readVatClassification(service),
});

const ServiceService = {
  toFormService,

  getServiceManagementData: async (storeId) => {
    const response = await api.get(`/stores/${storeId}`);
    const storeData = response.data;

    return {
      supportedLanguages: normalizeSupportedLanguages(storeData?.supportedLanguages),
      currency: storeData?.currency || DEFAULT_CURRENCY,
      stripePaymentReady: Boolean(storeData?.stripePaymentReady ?? storeData?.StripePaymentReady),
      appointmentCustomerPaymentMode: normalizeCustomerPaymentMode(
        storeData?.appointmentCustomerPaymentMode ?? storeData?.AppointmentCustomerPaymentMode,
      ),
      installmentMethodsAvailable: Boolean(
        storeData?.installmentMethodsAvailable ?? storeData?.InstallmentMethodsAvailable,
      ),
      services: Array.isArray(storeData?.services)
        ? storeData.services.map((service) => toFormService(service))
        : [],
    };
  },

  createService: async (storeId, service) => {
    const payload = buildServicePayload(service);
    const response = await api.post('/services', payload, { _storeId: storeId });
    return response.data;
  },

  updateService: async (storeId, serviceId, service) => {
    const payload = buildServicePayload(service, serviceId);
    const response = await api.put(`/services/${serviceId}`, payload, { _storeId: storeId });
    return response.data;
  },

  deleteService: async (storeId, serviceId) => {
    const response = await api.delete(`/services/${serviceId}`, { _storeId: storeId });
    return response.data;
  },

  getServiceRecipe: async (storeId, serviceId) => {
    const response = await api.get(`/services/${serviceId}/products`, { _storeId: storeId });
    const lines = Array.isArray(response.data)
      ? response.data
      : (response.data?.products ?? response.data?.lines ?? response.data?.items ?? []);

    return (lines || []).map((line) => ({
      productId: line?.productId ?? line?.ProductId,
      quantity: Number(line?.quantity ?? line?.Quantity) || 0,
      unitCost: line?.unitCost ?? line?.UnitCost ?? line?.lastUnitCost ?? line?.LastUnitCost ?? null,
      contentQuantity: Number(line?.contentQuantity ?? line?.ContentQuantity) || 1,
      name: line?.name ?? line?.Name ?? null,
      stockUnit: line?.stockUnit ?? line?.StockUnit ?? 'Unit',
    }));
  },
};

export default ServiceService;
